import React from 'react';
import { cn } from '@/utils/cn';
import { Input } from '@/components/ui/Input';
import type {
  MetaCampaignCreateStatus,
  MetaCampaignObjective,
} from '@/services/api/metaAds';

interface MetaObjectiveStepProps {
  campaignName: string;
  objective: MetaCampaignObjective | null;
  status: MetaCampaignCreateStatus;
  onChangeName: (name: string) => void;
  onChangeObjective: (objective: MetaCampaignObjective) => void;
  onChangeStatus: (status: MetaCampaignCreateStatus) => void;
  errors?: {
    name?: string;
    objective?: string;
  };
}

/**
 * Step 2 — campaign name + ODAX objective + initial status.
 *
 * The six OUTCOME_* objectives are the current Meta ad set. Legacy enums
 * (BRAND_AWARENESS / LINK_CLICKS / CONVERSIONS / etc.) are deprecated under
 * Meta's ODAX transition; the backend Pydantic model rejects them too.
 *
 * Initial status mirrors Google's `CampaignBasicsStep` "Initial Status *"
 * field — PAUSED is the recommended default, but ACTIVE is exposed so a
 * user populating ad sets elsewhere can launch immediately.
 */

const STATUS_OPTIONS: Array<{
  value: MetaCampaignCreateStatus;
  label: string;
  description: string;
}> = [
  {
    value: 'PAUSED',
    label: 'Paused',
    description:
      'Recommended for new campaigns. Enable it after adding ad sets and ads.',
  },
  {
    value: 'ACTIVE',
    label: 'Active',
    description:
      'Campaign will start serving as soon as ad sets, ads, and creatives exist.',
  },
];

// OUTCOME_TRAFFIC sits first because it's the wizard's pre-selected
// default (analogous to Google's `campaignType: 'SEARCH'`). Keeping the
// pre-selected option at the top of the list means a user who agrees
// with the default sees it visually highlighted at the top — no scanning
// required.
//
// DEFERRED OBJECTIVES (commented out below — do not delete):
//
// `OUTCOME_LEADS` and `OUTCOME_APP_PROMOTION` are wired end-to-end in
// the wizard (AdSetPromotedObjectStep handles both, backend models
// validate both, matrices in metaAdSetMatrix.ts list both) but hidden
// from the UI picker for the initial Meta App Review submission
// (2026-06-22). Rationale per META_INTEGRATION_PLAN.md §17:
//
//   - OUTCOME_LEADS — `leads_retrieval` and `pages_manage_metadata`
//     scopes were dropped from the App Review queue. The Lead Form
//     picker may degrade without `pages_manage_metadata`, and Flouri's
//     near-term roadmap has no lead-ad customer. Re-enable when there's
//     a real customer and after smoke-testing the lead-form picker.
//
//   - OUTCOME_APP_PROMOTION — Flouri has no test app to demo with, and
//     supporting the surface end-to-end requires Meta SDK integration
//     on the user's side that Flouri doesn't help with. Re-enable when
//     mobile-app advertising is on the roadmap.
//
// To re-enable: uncomment the relevant entry below + add the matching
// permission(s) back to META_OAUTH_SCOPES in
// src/api/services/meta_oauth_service_db.py +
// add the corresponding Use Case back to the Meta App Dashboard for a
// follow-up App Review submission. The wizard itself needs no other
// changes — matrices, type guards, AdSetPromotedObjectStep already
// handle both objectives.
const OBJECTIVES: Array<{
  value: MetaCampaignObjective;
  label: string;
  description: string;
}> = [
  {
    value: 'OUTCOME_TRAFFIC',
    label: 'Traffic',
    description: 'Send people to a destination — your website, app, or Messenger.',
  },
  {
    value: 'OUTCOME_SALES',
    label: 'Sales',
    description: 'Drive purchases — on your website, in your app, or in person.',
  },
  {
    value: 'OUTCOME_AWARENESS',
    label: 'Awareness',
    description: 'Show your ad to the most people, maximize reach and impressions.',
  },
  {
    value: 'OUTCOME_ENGAGEMENT',
    label: 'Engagement',
    description: 'Get more messages, video views, post engagement, or page likes.',
  },
  {
    value: 'OUTCOME_LEADS',
    label: 'Leads',
    description: 'Collect leads via forms, messages, calls, or signups.',
  },
  // {
  //   value: 'OUTCOME_APP_PROMOTION',
  //   label: 'App promotion',
  //   description: 'Find new app users or re-engage existing ones.',
  // },
];

export const MetaObjectiveStep: React.FC<MetaObjectiveStepProps> = ({
  campaignName,
  objective,
  status,
  onChangeName,
  onChangeObjective,
  onChangeStatus,
  errors,
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Campaign Basics
        </h3>
        <p className="text-sm text-gray-600">
          Give your campaign a name and choose what it should optimize for.
        </p>
      </div>

      <div>
        <label
          htmlFor="meta-campaign-name"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Campaign name <span className="text-red-500">*</span>
        </label>
        <Input
          id="meta-campaign-name"
          type="text"
          value={campaignName}
          onChange={(e) => onChangeName(e.target.value)}
          maxLength={400}
          placeholder="e.g. Summer Sale Q3"
          aria-invalid={Boolean(errors?.name)}
          aria-describedby={errors?.name ? 'meta-campaign-name-error' : undefined}
        />
        {errors?.name && (
          <p
            id="meta-campaign-name-error"
            role="alert"
            className="text-sm text-red-600 mt-1"
          >
            {errors.name}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          Choose a descriptive name to identify this campaign. {campaignName.length}/400 characters.
        </p>
      </div>

      <div>
        <p className="block text-sm font-medium text-gray-700 mb-3">
          Objective <span className="text-red-500">*</span>
        </p>
        <div role="radiogroup" aria-label="Campaign objective">
          {OBJECTIVES.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={objective === opt.value}
              onClick={() => onChangeObjective(opt.value)}
              className={cn(
                'w-full text-left p-4 border-2 rounded-lg mb-2 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500',
                objective === opt.value
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:bg-gray-50 bg-white',
              )}
            >
              <div className="text-sm font-medium text-gray-900">{opt.label}</div>
              <div className="text-xs text-gray-500 mt-1">{opt.description}</div>
            </button>
          ))}
        </div>
        {errors?.objective && (
          <p role="alert" className="text-sm text-red-600 mt-2">
            {errors.objective}
          </p>
        )}
      </div>

      <div>
        <p className="block text-sm font-medium text-gray-700 mb-3">
          Initial Status <span className="text-red-500">*</span>
        </p>
        <div role="radiogroup" aria-label="Initial status">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={status === opt.value}
              onClick={() => onChangeStatus(opt.value)}
              className={cn(
                'w-full text-left p-4 border-2 rounded-lg mb-2 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500',
                status === opt.value
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:bg-gray-50 bg-white',
              )}
            >
              <div className="text-sm font-medium text-gray-900">{opt.label}</div>
              <div className="text-xs text-gray-500 mt-1">{opt.description}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
